import { library } from '@fortawesome/fontawesome-svg-core';
import { faChartBar } from '@fortawesome/free-solid-svg-icons';
import { TranslationKey, useTranslator } from '@Localization';
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  TooltipItem,
  TooltipModel,
} from 'chart.js';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Bar } from 'react-chartjs-2';
import { IAnnotationStatusStatisticsDto } from '../projectDetailsStatistics.model';

ChartJS.register(CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend);

library.add(faChartBar);

interface IAnnotationProgressChartProps {
  title: TranslationKey;
  annotationStatuses?: IAnnotationStatusStatisticsDto[];
  translateNames?: boolean;
}

export const AllStatusChart = observer((props: IAnnotationProgressChartProps): ReactElement => {
  const { t } = useTranslator();
  const { annotationStatuses, title, translateNames } = props;

  if (!annotationStatuses) {
    return <></>;
  }

  const sorted = annotationStatuses.slice();

  const style = getComputedStyle(document.body);

  const attributeData = {
    labels: sorted.map((a) => (translateNames ? t(a.name) : a.name)),
    datasets: [
      {
        label: t('awaiting_review'),
        data: sorted.map((a) => a.toReview),
        backgroundColor: style.getPropertyValue('--joy-palette-primary-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-primary-500'),
      },
      {
        label: t('accepted'),
        data: sorted.map((a) => a.accepted),
        backgroundColor: style.getPropertyValue('--joy-palette-success-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-success-500'),
      },
      {
        label: t('rejected'),
        data: sorted.map((a) => a.rejected),
        backgroundColor: style.getPropertyValue('--joy-palette-danger-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-danger-500'),
      },
    ],
  };

  return (
    <div className='statistic-wrapper'>
      <div className='statistic-wrapper-header'>{t(title)}</div>
      <div className='statistics-body'>
        <div className='statistics-summary'>
          <>
            {sorted.map((a) => (
              <div key={a.id}>
                {translateNames ? t(a.name) : a.name}: {a.accepted + a.toReview + a.rejected}
              </div>
            ))}
          </>
        </div>
        <div className='chart-wrapper'>
          <div className='statistics-chart wide'>
            <Bar
              data={attributeData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'right',
                    align: 'center',
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                      label(this: TooltipModel<'bar'>, tooltipItem: TooltipItem<'bar'>): string {
                        return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    grid: { display: false },
                    stacked: true,
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMin: 0,
                    stacked: true,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
